// Starter boards. Each one is honest on day one: local widgets (clocks, notes,
// tasks, timers) work immediately, and data widgets wait for real data with a
// clear way to send it. No template ships a number nobody sent. Every data
// widget is one a service fills (shared/fits.ts): MRR from Lemon Squeezy or
// Stripe, visitors and pages from Plausible, subscribers from beehiiv or
// Buttondown, so Connect on it connects the service and fills it in one go.

import type { Board, ItemStyle, ThemeSettings, WidgetType } from "./types";

export interface TemplateWidget {
  type: WidgetType;
  title: string;
  x: number;
  y: number;
  w: number;
  h: number;
  stream?: string;
  props?: Record<string, unknown>;
  style?: ItemStyle;
}

export interface TemplateInk {
  x: number;
  y: number;
  w: number;
  h: number;
  ink: import("./types").InkShape;
}

export interface TemplatePull {
  preset: string;
  params: Record<string, string>;
  stream: string;
  name: string;
  every: number;
}

export interface TemplateSpec {
  id: string;
  name: string;
  emoji: string;
  blurb: string;
  theme: ThemeSettings;
  widgets: TemplateWidget[];
  ink?: TemplateInk[];
  pulls?: TemplatePull[];
}

const tasks = (...t: string[]) => t.map((text, i) => ({ id: `t${i + 1}`, text, done: false }));

/** A note in the board's hand, above the widget it points to. */
const note = (x: number, y: number, w: number, text: string): TemplateInk => ({ x, y, w, h: 56, ink: { t: "text", text, color: "auto", fontSize: 19, font: "hand", opacity: 0.8 } });

export const TEMPLATES: TemplateSpec[] = [
  {
    id: "founder",
    name: "Founder HQ",
    emoji: "🛰️",
    blurb: "Revenue, signups and your agents' work, on one calm screen.",
    theme: { id: "meadow" },
    widgets: [
      { type: "heading", title: "Heading", x: 0, y: 0, w: 520, h: 72, props: { text: "This week", align: "left" } },
      { type: "metric", title: "MRR", x: 0, y: 96, w: 280, h: 176, stream: "mrr", props: { compare: "auto", goodWhen: "up", spark: true } },
      { type: "metric", title: "Visitors (30d)", x: 0, y: 296, w: 280, h: 176, stream: "visitors", props: { compare: "auto", goodWhen: "up", spark: true } },
      { type: "chart", title: "MRR over time", x: 304, y: 96, w: 448, h: 264, stream: "mrr", props: { chart: "area", range: "90d", axis: true } },
      { type: "goal", title: "Road to $10k", x: 304, y: 384, w: 448, h: 184, stream: "mrr", props: { target: 10000, start: 0, style: "bar" } },
      // whichever agent the user connects, not one they might never use
      { type: "agent", title: "Agent activity", x: 776, y: 96, w: 344, h: 208, props: { agent: "" } },
      { type: "feed", title: "Board activity", x: 776, y: 328, w: 344, h: 400, props: { source: "activity", max: 30 } },
      {
        type: "tasks",
        title: "Today",
        x: 1144,
        y: 96,
        w: 320,
        h: 296,
        props: { tasks: tasks("Talk to one customer", "Ship one small thing", "Write down what you learned") },
      },
      { type: "notes", title: "Focus", x: 1144, y: 416, w: 320, h: 216, props: { text: "What matters most this week?\n\n" } },
      { type: "list", title: "Top pages", x: 0, y: 496, w: 280, h: 296, stream: "top-pages", props: { max: 6, bars: true } },
      { type: "clock", title: "Clock", x: 1144, y: 656, w: 160, h: 136, props: { face: "digital", seconds: false } },
      { type: "timer", title: "Focus timer", x: 1328, y: 656, w: 136, h: 136, props: { work: 25, rest: 5 }, style: { header: false } },
    ],
    ink: [
      {
        x: 790,
        y: 742,
        w: 300,
        h: 56,
        ink: { t: "text", text: "your agents report here ↑", color: "auto", fontSize: 26, font: "hand", opacity: 0.85 },
      },
    ],
  },
  {
    id: "launch",
    name: "Launch week",
    emoji: "🚀",
    blurb: "A countdown, a goal and the checklist, for the week it all happens.",
    theme: { id: "summit" },
    widgets: [
      { type: "countdown", title: "Launch", x: 0, y: 0, w: 336, h: 200, props: { to: "", label: "Launch day" } },
      { type: "goal", title: "Subscribers goal", x: 360, y: 0, w: 280, h: 200, stream: "subscribers", props: { target: 500, start: 0, style: "ring" } },
      { type: "metric", title: "Visitors today", x: 664, y: 0, w: 280, h: 200, stream: "visitors-today", props: { compare: "1d", spark: true } },
      {
        type: "tasks",
        title: "Launch checklist",
        x: 0,
        y: 224,
        w: 336,
        h: 384,
        props: {
          tasks: tasks(
            "Landing page live and fast",
            "Pricing and checkout tested end to end",
            "Launch post written",
            "Demo video recorded",
            "Ten people lined up to share it",
            "Analytics checked on the real domain",
          ),
        },
      },
      { type: "chart", title: "Subscribers", x: 360, y: 224, w: 584, h: 264, stream: "subscribers", props: { chart: "area", range: "30d", axis: true } },
      { type: "list", title: "Where they came from", x: 360, y: 512, w: 584, h: 280, stream: "referrers", props: { max: 6, bars: true } },
      { type: "feed", title: "Hacker News", x: 968, y: 0, w: 360, h: 480, stream: "hn-show", props: { source: "stream", max: 12 } },
      { type: "notes", title: "Launch notes", x: 968, y: 504, w: 360, h: 288, props: { text: "" } },
    ],
    pulls: [{ preset: "hn", params: { list: "show" }, stream: "hn-show", name: "Show HN", every: 1800 }],
  },
  {
    // made for every new account beside its first board (WorkspaceDO.init), and in New board for any account
    id: "agents",
    name: "Agents",
    emoji: "🤖",
    blurb: "Every Claude Code and Codex session on your computers, what waits for you, and how much of your plans is left.",
    theme: { id: "space" },
    widgets: [
      { type: "agent", title: "Agent activity", x: 0, y: 64, w: 560, h: 400, props: { agent: "" } },
      { type: "feed", title: "Board activity", x: 0, y: 564, w: 560, h: 380, props: { source: "activity", max: 40 } },
      { type: "needsyou", title: "Needs you", x: 600, y: 64, w: 380, h: 260 },
      { type: "usage", title: "AI usage", x: 600, y: 424, w: 380, h: 320 },
      { type: "brief", title: "Morning brief", x: 1020, y: 64, w: 480, h: 400, stream: "brief" },
      { type: "gitlog", title: "Git activity", x: 1020, y: 564, w: 480, h: 300 },
    ],
    ink: [
      note(0, 0, 560, "Every Claude Code and Codex session on your computers,\nwith what waits for you first ↓"),
      note(0, 500, 560, "What happened on this board, newest first ↓"),
      note(600, 0, 380, "A question or an OK your\nsessions are waiting on ↓"),
      note(600, 360, 380, "How much of your Claude Code\nand Codex plans is left ↓"),
      note(1020, 0, 480, "Your agent writes it from your board.\nSet it to arrive every morning ↓"),
      note(1020, 500, 480, "Today's commits in your project folders ↓"),
    ],
  },
  {
    id: "desk",
    name: "Calm desk",
    emoji: "🌙",
    blurb: "A clock, the time around the world, a focus timer and your day. Works the moment it opens.",
    theme: { id: "ember" },
    widgets: [
      { type: "clock", title: "Clock", x: 0, y: 0, w: 288, h: 288, props: { face: "analog", seconds: true }, style: { header: false } },
      { type: "clock", title: "World", x: 312, y: 0, w: 360, h: 312, props: { face: "digital", zones: ["America/Los_Angeles", "America/New_York", "Europe/London", "Asia/Tokyo"] } },
      { type: "timer", title: "Focus", x: 696, y: 0, w: 264, h: 312, props: { work: 25, rest: 5 } },
      { type: "calendar", title: "Calendar", x: 984, y: 0, w: 264, h: 312, props: {} },
      { type: "notes", title: "Today", x: 0, y: 336, w: 400, h: 296, props: { text: "" }, style: { tile: "paper" } },
      { type: "tasks", title: "Three things", x: 424, y: 336, w: 344, h: 296, props: { tasks: [] } },
      { type: "habits", title: "Habits", x: 792, y: 336, w: 456, h: 232, props: { habits: [{ id: "h1", name: "Shipped something" }, { id: "h2", name: "Posted" }, { id: "h3", name: "Moved" }], log: {} } },
    ],
  },
  {
    id: "blank",
    name: "Blank canvas",
    emoji: "✨",
    blurb: "Nothing on it yet. Ask your agent to build it with you.",
    theme: { id: "studio" },
    widgets: [],
  },
];

export const TEMPLATE: Record<string, TemplateSpec> = Object.fromEntries(TEMPLATES.map((t) => [t.id, t]));

export function defaultShare(): Board["share"] {
  return { enabled: false, slug: null, badge: true };
}
